import { useId } from "react";

// Set square, pencil and baseline. The tile paints the cut-outs in the tile
// colour over the glyph; the bare glyph masks them out instead.
const PRECON_GLYPH_PATHS = [
  "M12.5795 8L29.0795 24.5H12.5795V8Z",
  "M7.65 8.87097H9.76223C10.1956 8.87097 10.4122 9.08764 10.4122 9.52097V11.321H7V9.52097C7 9.08764 7.21667 8.87097 7.65 8.87097Z",
  "M7 12.2C7 12.0895 7.08954 12 7.2 12H10.21C10.3205 12 10.41 12.0895 10.41 12.2V19.5949C10.41 19.7054 10.3205 19.7949 10.21 19.7949H7.2C7.08954 19.7949 7 19.7054 7 19.5949V12.2Z",
  "M7.14852 21.0619C7.08059 20.9289 7.17724 20.771 7.32665 20.771H10.0856C10.235 20.771 10.3316 20.9289 10.2637 21.0619L8.88425 23.7642C8.81001 23.9096 8.60222 23.9096 8.52798 23.7642L7.14852 21.0619Z",
  "M8.04068 27C8.51524 27.0639 10.0317 27.0574 10.5507 27.0583L14.9721 27.0589C19.6481 27.077 24.3241 27.0678 29 27.0314C28.9872 27.4796 28.9651 27.9508 28.9879 28.3967C25.3658 28.37 21.7436 28.3631 18.1215 28.3761C14.7482 28.3566 11.375 28.3646 8.00187 28.4C7.9981 27.8935 7.99559 27.5075 8.04068 27Z"
];

const PRECON_CUTOUT_PATHS = [
  "M12.5795 12H14.3795V12.65H12.5795V12ZM12.5795 14H13.7295V14.65H12.5795V14ZM12.5795 16H14.3795V16.65H12.5795V16ZM12.5795 18H13.7295V18.65H12.5795V18ZM12.5795 20H14.3795V20.65H12.5795V20ZM16.0795 22.7H16.7295V24.5H16.0795V22.7ZM18.0795 23.35H18.7295V24.5H18.0795V23.35ZM20.0795 22.7H20.7295V24.5H20.0795V22.7ZM22.0795 23.35H22.7295V24.5H22.0795V23.35ZM24.0795 22.7H24.7295V24.5H24.0795V22.7Z",
  "M16.5795 15.1L21.8795 20.4H16.5795V15.1Z",
  "M21.1191 25.653C21.3266 25.6812 21.945 25.6695 22.1765 25.6692L24.1966 25.6649C23.7518 26.083 23.2368 26.4936 22.7677 26.9301C22.5459 27.1342 22.2699 27.3558 22.0395 27.5612C21.8158 27.7606 21.3997 28.1783 21.1557 28.3102L21.11 28.2889C21.0756 28.0589 21.098 27.575 21.1014 27.3182L21.1191 25.653Z"
];

/**
 * Precon glyph — drafting tools, no tile. A filled shape, so the colour comes
 * from a `fill-*` class in `className`.
 */
export default function PreconCiqIcon({
  size = 12,
  className = "fill-black",
  opacity = "0.6"
}: {
  size?: number;
  className?: string;
  opacity?: string;
}) {
  // Per-instance id so several icons on one page don't share a mask.
  const maskId = `precon-mask-${useId().replace(/:/g, "")}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="6 6.2 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <defs>
        <mask
          id={maskId}
          maskUnits="userSpaceOnUse"
          x="6"
          y="6.2"
          width="24"
          height="24"
        >
          <rect x="6" y="6.2" width="24" height="24" fill="white" />
          {PRECON_CUTOUT_PATHS.map((d) => (
            <path key={d} d={d} fill="black" />
          ))}
        </mask>
      </defs>
      <g mask={`url(#${maskId})`} fillOpacity={opacity}>
        {PRECON_GLYPH_PATHS.map((d) => (
          <path key={d} d={d} />
        ))}
      </g>
    </svg>
  );
}

/**
 * Precon phase badge — the same drafting tools on their fixed-colour tile.
 * Design: 36×36 container (#2E3199) + glyph (#D746FF). Used by CiqIconBox.
 */
export function PreconTileCiqIcon({
  size = 36,
  className = ""
}: {
  size?: number;
  className?: string;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 36 36"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M0 4C0 1.79086 1.79086 0 4 0H32C34.2091 0 36 1.79086 36 4V32C36 34.2091 34.2091 36 32 36H4C1.79086 36 0 34.2091 0 32V4Z"
        fill="#2E3199"
      />
      {PRECON_GLYPH_PATHS.map((d) => (
        <path key={d} d={d} fill="#D746FF" />
      ))}
      {PRECON_CUTOUT_PATHS.map((d) => (
        <path key={d} d={d} fill="#2E3199" />
      ))}
    </svg>
  );
}
